import {
  ButtonComponent,
  Translate,
  DropdownItem,
  IconComponent,
  AbstractModalContent,
  AppContext,
} from '@galigeo-store/ui-lib';
import type { TableCell } from '@galigeo-store/ui-lib';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { GaligeoCloudService } from '../../services/galigeo-cloud.service';
import { MenuService } from '../../services/menu-service';
import { LoggerClient } from '../../services/logger.client';

@customElement('menu-modal')
export class MapModalComponent extends AbstractModalContent {
  @property({ type: Boolean }) categoriesEditable = false;
  @property() category: string | undefined | null = null;
  @property({ type: Array }) menu: any[] = [];
  @property({ type: Array }) _allMaps: any[] = [];
  @property({ type: Boolean }) editionEnabled = false;

  @state() private _spinner = false;
  @state() private _categoriesForFilter: any[] = [];

  private maps: any[] = [];
  private categories: any[] = [];

  constructor() {
    super();

    //this.draw();

    this.addEventListener('refresh', async () => {
      setTimeout(async () => {
        this._allMaps = [];
        await this.draw();
      }, 1000);
    });
  }

  protected updated(changedProperties: Map<string, any>): void {
    super.updated(changedProperties);
    if ((changedProperties.has('_allMaps') || changedProperties.has('menu')) && this._allMaps !== null) {
      this.draw();
    }
  }

  async draw() {
    this._spinner = true;
    if (this._allMaps.length === 0) {
      let maps = await GaligeoCloudService.getMaps({ updateCategories: true });
      this._allMaps = maps;
    }
    this.categories = await MenuService.getCategories();
    this._categoriesForFilter = [...this.categories];
    if (!this._categoriesForFilter.find(c => c.id === null)) {
      this._categoriesForFilter.push({
        id: null,
        label: Translate.get('uncategorized'),
      });
    }

    this.applyFilters();
    this._spinner = false;
    this.requestUpdate();
  }

  private applyFilters(
    searchValue: string = '',
    categoryId: string | undefined | null = this.category,
  ) {
    let filteredMaps = this._allMaps;

    filteredMaps.sort((a, b) => {
      if (a.categoryId && !b.categoryId) return -1;
      if (!a.categoryId && b.categoryId) return 1;
      return 0;
    });
    if (categoryId === null) {
      filteredMaps = filteredMaps.filter((map) => !map.categoryId);
    } else if (categoryId !== undefined) {
      filteredMaps = filteredMaps.filter(
        (map) => map.categoryId === categoryId,
      );
    }
    if (searchValue) {
      filteredMaps = filteredMaps.filter((map) =>
        map.label.toLowerCase().includes(searchValue.toLowerCase()),
      );
    }
    this.maps = filteredMaps;
    this.requestUpdate();
  }

  private getCategoryForFilter(categoryId: string | undefined | null) {
    if (categoryId === undefined) {
      return undefined;
    }
    return this._categoriesForFilter.find((c) => c.id === categoryId);
  }

  private getCategory(categoryId: string | undefined | null) {
    if (categoryId === undefined) {
      return undefined;
    }
    return this.categories.find((c) => c.id === categoryId);
  }

  private handleClick(map: any) {
    console.log('Map clicked:', map);
    if (this.maps[map.detail.y]) {
      this.dispatchEvent(
        new CustomEvent('map-clicked', {
          detail: {
            map: this.maps[map.detail.y],
            action: 'open',
            closeModal: true,
            newTab: map.detail.newTab,
          },
        }),
      );
    }
  }

  private handleChangeCategory(map: any, itemId: string) {
    if (!itemId) {
      this.removeMapFromCategory(map);
    } else {
      this.updateMapCategory(map, itemId);
    }
    MenuService.saveMenu(this.menu);
  }

  private removeMapFromCategory(map: any) {
    if (map.categoryId) {
      const category = this.menu.find((item) => item.id === map.categoryId);
      if (category) {
        category.maps = category.maps.filter((m: any) => m.id !== map.id);
      }
    }
    map.categoryId = null;
    this.requestUpdate();
  }

  private updateMapCategory(map: any, itemId: string) {
    let hasCategory = false;
    this.menu.forEach((item) => {
      item.maps = item.maps.filter((m: any) => m.id !== map.id);
      if (item.id === itemId) {
        map.categoryId = item.id;
        item.maps.push(map);
        hasCategory = true;
      }
    });
    if (!hasCategory) {
      // If the category does not exist, create it
      const newCategory = {
        id: itemId,
        label: itemId,
        maps: [map],
      };
      this.menu.push(newCategory);
      map.categoryId = itemId;
    }
    LoggerClient.log('update-category', itemId);
  }

  private async handleAddMap() {
    this.dispatchEvent(
      new CustomEvent('map-clicked', {
        detail: { category: this.category, action: 'add', closeModal: false },
      }),
    );
    LoggerClient.log('add-map', '');
  }

  render() {
    if (this._spinner) {
      return html`<ggo-spinner></ggo-spinner>`;
    }

    const fields = [
      //{ name: '' },
      {
        name: `${Translate.get('map_modal_type')}`,
        sortable: true,
        visible: false,
      },
      { name: `${Translate.get('map_modal_name')}`, sortable: true },
      { name: `${Translate.get('map_modal_theme')}`, sortable: true },
      { name: ``, sortable: false },
    ];

    const rows = this.maps
      .filter((map) => !this.category || map.categoryId === this.category)
      .map((map) => {
        console.log('Rendering map:', map.label);
        return [
          //this.renderPrivateIcon(map),
          this.renderIconType(map),
          this.renderMapName(map),
          this.renderThemeCombos(map),
          this.renderOptionsDropDown(map),
        ];
      });

    const showCategoryFilter = this.categoriesEditable;

    const selectedCategoryForFilter = this.getCategoryForFilter(this.category);
    const categoryFilterTemplate = showCategoryFilter
      ? html`
          <div class="slds-align-middle">
            <ggo-combobox
              placeHolder="${Translate.get('filter_by_category')}"
              separatorIndex=${this._categoriesForFilter.length - 2}
              .values=${this._categoriesForFilter}
              .selectedValue=${selectedCategoryForFilter}
              @changed=${(e: CustomEvent) => {
          const id = e.detail?.id;
          this.category = id;
          this.applyFilters('', id);
          this.dispatchEvent(new CustomEvent('changed', { detail: { id } }));
        }}
            ></ggo-combobox>
          </div>
        `
      : nothing;

    return html`
          <div class="slds-grid slds-grid_align-spread">
            <div class="slds-align-middle slds-grid">
            ${this.editionEnabled
        ? html`<ggo-button
                  @click="${this.handleAddMap}"
                  icon="add"
                  size="small"
                  title="${Translate.get('map_modal_add_map')}"
                  label="${Translate.get('map_modal_add_map')}"
                  class="slds-m-right_small"
                  style="margin-left: auto;"
                  variant="${ButtonComponent.VALIDATE}"
                ></ggo-button>` : nothing}
              <ggo-text-input
                class="slds-align-middle"
                placeholder="${Translate.get('search')}"
                @text-input="${(e: CustomEvent) => {
        const searchValue = e.detail;
        this.applyFilters(searchValue, this.category);
      }}"
              />
            </div>
            ${categoryFilterTemplate}
          </div>
      <div class="slds-m-top_small">
        ${this.maps.length === 0 && !this._spinner
        ? html`<div class="slds-text-align_center slds-m-top_medium">
              <span class="slds-text-title"
                >${Translate.get('no_results_found')}</span
              >
            </div>`
        : html`<ggo-table
              style=${this.getAttribute('style') ?? ''}
              .fields=${fields}
              .rows=${rows}
              @row-click=${this.handleClick}
              maxLines=${60}
              .rowTitle=${[
            Translate.get('map_modal_type'),
            Translate.get('map_modal_name'),
          ]}
            ></ggo-table>`}
      </div>
    `;
  }

  renderPrivateIcon(map: any) {
    if (map.private) {
      return html`<ggo-icon
        icon="lock"
        size="${IconComponent.XSMALL}"
      ></ggo-icon>`;
    } else {
      return html`<ggo-icon
        icon="unlock"
        size="${IconComponent.XSMALL}"
      ></ggo-icon>`;
    }
  }

  renderIconType(map: any) {
    if (map.type === 'map') {
      return html`<ggo-icon
        title="${Translate.get('map_modal_map')}"
        icon="location"
        size="${IconComponent.XSMALL}"
      ></ggo-icon>`;
    } else {
      return html`<ggo-icon
        title="${Translate.get('map_modal_app')}"
        icon="light_bulb"
        size="${IconComponent.XSMALL}"
      ></ggo-icon>`;
    }
  }

  renderMapName(map: any): TableCell {
    return {
      cell: html`<span
        >${map.private
          ? html`<ggo-icon
              icon="lock"
              size="${IconComponent.XSMALL}"
            ></ggo-icon>`
          : nothing}<span class="slds-truncate">${map.label}</span></span
      >`,
      value: map.label,
    };
  }

  renderThemeCombos(map: any) {
    const category = this.getCategory(map.categoryId);
    if (this.editionEnabled && this.categoriesEditable) {
      return {
        cell: html` <ggo-combobox
          placeHolder="${Translate.get('no_category')}"
          .selectedValue="${category}"
          .values="${this.categories}"
          @changed="${(e: CustomEvent) => {
            e.stopPropagation();
            this.handleChangeCategory(map, e.detail?.id);
          }}"
          @click="${(e: Event) => e.stopPropagation()}"
        ></ggo-combobox>`,
        value: category?.label,
      };
    } else if (!this.editionEnabled) {
      return {
        cell: html`<span>${category?.label ?? Translate.get('uncategorized')}</span>`,
        value: category?.label,
      };
    }
  }

  renderOptionsDropDown(map: any) {
    if (!this.editionEnabled) {
      return nothing;
    }
    const isAuthor =
      AppContext.getCurrentUser().capabilities.indexOf('author') >= 0;

    const dropdownItems: DropdownItem[] = [];
    if (isAuthor) {
      dropdownItems.push({
        id: 'delete',
        text: Translate.get('map_modal_delete_map'),
        icon: 'utility:delete',
        dividerAfter: false,
      });
    }
    dropdownItems.push(
      {
        id: 'rights',
        text: Translate.get('map_modal_manage_rights'),
        icon: 'utility:lock',
        dividerAfter: false,
      },
      {
        id: 'duplicate',
        text: Translate.get('map_modal_duplicate_map'),
        icon: 'utility:copy',
        dividerAfter: false,
      },
      {
        id: 'edit_name',
        text: Translate.get('map_modal_edit_name'),
        icon: 'utility:edit',
        dividerAfter: false,
      },
    );
    return html`<ggo-dropdown
      icon="threedots"
      .items="${dropdownItems}"
      @click="${(e: CustomEvent) => {
        console.log(e);
        this.dispatchEvent(
          new CustomEvent('map-clicked', {
            detail: { map: map, action: e.detail.id, closeModal: false },
          }),
        );
      }}"
    >
    </ggo-dropdown>`;
  }
}
